/**
 * 账户模块 API
 *
 * 覆盖 OpenAPI tags：Announcements / Wallet / Withdrawals / Subscriptions / Settings
 */

import { get, post, put } from '../client';
import type { ApiResult } from '../types/common';
import type {
  WalletChallengeResult,
  CreateWithdrawalRequest,
  CreateWithdrawalResult,
  SubscriptionPlan,
  Subscription,
  PendingPlan,
  SubscribeRequest,
  SubscribeResult,
  SubscriptionChangeRequest,
  SubscriptionChangeResult,
  SubscriptionCancelResult,
  SubscriptionOrderListResult,
  CancelSubscriptionOrderResult,
  UserSettings,
  UpdateNotificationEmailRequest,
  UpdateEmailSubscriptionRequest,
  UpdateEmailSubscriptionResult,
  AnnouncementListQueryParams,
  AnnouncementPage,
  Announcement,
} from './account';

// ==================== 公告 ====================

/**
 * 获取启用公告列表。
 *
 * 对应 OpenAPI: GET /announcements
 *
 * @param params.page 页码（可选，从 1 开始）
 * @param params.limit 每页数量（可选，1-100）
 * @requiresAuth 需要 Bearer Token 认证
 */
export async function listAnnouncements(
  params?: AnnouncementListQueryParams
): Promise<ApiResult<AnnouncementPage>> {
  const search = new URLSearchParams();
  if (params?.page) search.set('page', String(params.page));
  if (params?.limit) search.set('limit', String(params.limit));
  const qs = search.toString();
  const url = qs ? `/announcements?${qs}` : '/announcements';
  return get<AnnouncementPage>(url, { requiresAuth: true });
}

/**
 * 获取启用公告详情。
 *
 * 对应 OpenAPI: GET /announcements/{id}
 *
 * @param id 公告 ID（YAML path 参数为 integer）
 * @requiresAuth 需要 Bearer Token 认证
 */
export async function getAnnouncement(id: number): Promise<ApiResult<Announcement>> {
  return get<Announcement>(`/announcements/${id}`, { requiresAuth: true });
}

// ==================== 钱包挑战 ====================

/**
 * 获取钱包操作挑战 nonce
 *
 * 对应 OpenAPI: POST /wallet/challenge
 *
 * 用于订阅变更、提现等需钱包签名授权的操作。
 * 客户端获取 message 后使用 personal_sign 签名，再调用目标接口。
 *
 * @requiresAuth 需要 Bearer Token 认证
 *
 * @example
 * ```ts
 * const res = await getWalletChallenge();
 * if (res.ok) {
 *   const signature = await provider.request({
 *     method: 'personal_sign',
 *     params: [res.data.message, walletAddress],
 *   });
 * }
 * ```
 */
export async function getWalletChallenge(): Promise<ApiResult<WalletChallengeResult>> {
  return post<WalletChallengeResult>('/wallet/challenge', undefined, { requiresAuth: true });
}

// ==================== 提现 ====================

/**
 * 申请提现
 *
 * 对应 OpenAPI: POST /withdrawals
 *
 * 提现金额需 ≤ 当前可用余额，且需先通过 `/wallet/challenge` 获取 message
 * 用钱包签名后再调用本接口。
 *
 * @param data.amount 提现金额（USDT 字符串，最多 6 位小数）
 * @param data.signature 钱包签名
 * @requiresAuth 需要 Bearer Token 认证
 */
export async function createWithdrawal(
  data: CreateWithdrawalRequest
): Promise<ApiResult<CreateWithdrawalResult>> {
  return post<CreateWithdrawalResult>('/withdrawals', data, { requiresAuth: true });
}

// ==================== 订阅 ====================

/**
 * 获取可用订阅计划列表
 *
 * 对应 OpenAPI: GET /subscriptions/plans
 *
 * @requiresAuth 需要 Bearer Token 认证
 */
export async function getSubscriptionPlans(): Promise<ApiResult<SubscriptionPlan[]>> {
  return get<SubscriptionPlan[]>('/subscriptions/plans', { requiresAuth: true });
}

/**
 * 获取当前订阅信息
 *
 * 对应 OpenAPI: GET /subscriptions/current
 *
 * 若用户从未订阅，后端会自动初始化为免费计划订阅后返回，
 * 因此 data 在绝大多数情况下非 null（yaml 上 data 标记为 nullable）。
 *
 * @requiresAuth 需要 Bearer Token 认证
 */
export async function getCurrentSubscription(): Promise<ApiResult<Subscription | null>> {
  return get<Subscription | null>('/subscriptions/current', { requiresAuth: true });
}

/**
 * 获取下一周期的待生效订阅计划
 *
 * 对应 OpenAPI: GET /subscriptions/pending-plan
 *
 * 返回用户通过 `POST /subscriptions/change`（降级）或 `POST /subscriptions/cancel`
 * 设置的、将在当前计费周期结束时生效的计划详情。
 *
 * - `isFree=true`：用户已设置取消订阅（下周期降级为免费计划）
 * - `null`：未设置 pendingPlan（即 subscriptionState 不是 paid_pending_change）
 *
 * 注意：本数据已包含在 `getCurrentSubscription()` 返回的 `Subscription.pendingPlan`
 * 中，本接口仅在需要单独查询 / 轮询时使用，避免在主流程中重复请求。
 *
 * @requiresAuth 需要 Bearer Token 认证
 */
export async function getPendingPlan(): Promise<ApiResult<PendingPlan | null>> {
  return get<PendingPlan | null>('/subscriptions/pending-plan', {
    requiresAuth: true,
  });
}

/**
 * 取消订阅（下个周期生效，自动降级为免费计划）
 *
 * 对应 OpenAPI: POST /subscriptions/cancel（SignatureOnlyRequest）
 *
 * 调用前需通过 `getWalletChallenge` 获取 message 并用钱包签名。
 *
 * @param data.signature 钱包签名（personal_sign）
 * @returns `{ cancelled: true, effectiveAt }`
 * @requiresAuth 需要 Bearer Token 认证
 */
export async function cancelSubscription(
  data: { signature: string }
): Promise<ApiResult<SubscriptionCancelResult>> {
  return post<SubscriptionCancelResult>('/subscriptions/cancel', data, {
    requiresAuth: true,
  });
}

/**
 * 变更订阅计划（升级立即生效 / 降级下周期生效 / 余额不足生成 pending_order）
 *
 * 对应 OpenAPI: POST /subscriptions/change（SubscriptionChangeRequest）
 *
 * 三种结果（按 `result` 字段分支）：
 * - **HTTP 200 / `result='activated'`**：升级成功，新计划已立即生效，
 *   计费周期重置（periodStartAt = 升级时刻），积分继承（旧剩余 + 新配额）
 * - **HTTP 200 / `result='scheduled'`**：降级成功（含降到 free），
 *   待生效计划已设置，将在当前周期 periodEndAt 时切换
 * - **HTTP 402 / `result='pending_order'`**：余额不足，已创建 15 分钟有效期
 *   的待支付变更订单（type=upgrade 或 downgrade），充值差额后由后端自动完成变更
 *
 * 仅适用于当前 `subscriptionState='paid_active'` 或 `'paid_pending_change'`
 * 的用户；免费计划用户请使用 {@link subscribePlan}。
 *
 * 与 {@link subscribePlan} 一样，HTTP 402 是 yaml 约定的「余额不足、已创建待支付订单」
 * 成功语义，需通过 `acceptHttpStatus` 放行避免被 client 当作错误 throw。
 *
 * @param data.planId 目标计划 ID（字符串）
 * @param data.billingCycle 变更后的订阅付费周期；不传时后端默认 monthly
 * @param data.signature 钱包签名
 * @requiresAuth 需要 Bearer Token 认证
 */
export async function changeSubscription(
  data: SubscriptionChangeRequest
): Promise<ApiResult<SubscriptionChangeResult>> {
  return post<SubscriptionChangeResult>('/subscriptions/change', data, {
    requiresAuth: true,
    acceptHttpStatus: [402],
  });
}

/**
 * 订阅付费计划
 *
 * 对应 OpenAPI: POST /subscriptions/subscribe
 *
 * - 余额充足：即时扣款激活，HTTP 200
 * - 余额不足：创建待支付订单（1 小时有效），HTTP 402
 *
 * 注意：YAML 中 planId 为 integer 类型；billingCycle 不传时后端默认 monthly。
 *
 * @param data.planId 目标计划 ID（整数）
 * @param data.billingCycle 订阅付费周期
 * @param data.signature 钱包签名
 * @requiresAuth 需要 Bearer Token 认证
 */
export async function subscribePlan(
  data: SubscribeRequest
): Promise<ApiResult<SubscribeResult>> {
  // HTTP 402 是 YAML 约定的"余额不足、已创建待支付订单"的成功语义
  // （body 形如 { ok: true, data: { result: 'pending_order', ... } }），
  // 需通过 acceptHttpStatus 放行，避免被 client 当作错误 throw。
  return post<SubscribeResult>('/subscriptions/subscribe', data, {
    requiresAuth: true,
    acceptHttpStatus: [402],
  });
}

/**
 * 查询订阅订单列表（分页）
 *
 * 对应 OpenAPI: GET /subscriptions/orders
 *
 * 返回当前用户的订阅订单记录（包括 pending / paid / cancelled / expired）。
 *
 * @param params.page 页码（可选，从 1 开始）
 * @param params.limit 每页数量（可选）
 * @requiresAuth 需要 Bearer Token 认证
 */
export async function getSubscriptionOrders(
  params?: { page?: number; limit?: number }
): Promise<ApiResult<SubscriptionOrderListResult>> {
  const search = new URLSearchParams();
  if (params?.page) search.set('page', String(params.page));
  if (params?.limit) search.set('limit', String(params.limit));
  const qs = search.toString();
  const url = qs ? `/subscriptions/orders?${qs}` : '/subscriptions/orders';
  return get<SubscriptionOrderListResult>(url, { requiresAuth: true });
}

/**
 * 取消待支付订阅订单
 *
 * 对应 OpenAPI: POST /subscriptions/orders/{id}/cancel
 *
 * 仅 status=pending 的订单可取消，需提供钱包签名（先调用
 * `getWalletChallenge` 获取 message 再 personal_sign）。
 *
 * @param id 订阅订单 ID（YAML 类型为 integer）
 * @param data.signature 钱包签名
 * @requiresAuth 需要 Bearer Token 认证
 */
export async function cancelSubscriptionOrder(
  id: number,
  data: { signature: string }
): Promise<ApiResult<CancelSubscriptionOrderResult>> {
  return post<CancelSubscriptionOrderResult>(
    `/subscriptions/orders/${id}/cancel`,
    data,
    { requiresAuth: true }
  );
}

// ==================== 通知设置 ====================

/**
 * 获取当前设置
 *
 * 对应 OpenAPI: GET /settings
 *
 * 返回当前用户所有已配置的设置项（目前只有 alertEmail，未设置时为空字符串）。
 *
 * @requiresAuth 需要 Bearer Token 认证
 */
export async function getSettings(): Promise<ApiResult<UserSettings>> {
  return get<UserSettings>('/settings', { requiresAuth: true });
}

/**
 * 更新通知邮箱
 *
 * 对应 OpenAPI: PUT /settings/notification-email
 *
 * 用于设置低余额 / 低 Credits 告警的接收邮箱。
 * 设置邮箱时后端会自动开启 emailSubscribed（若之前为 false）。
 *
 * @param data.alertEmail 邮箱地址（需符合 email 格式）
 * @requiresAuth 需要 Bearer Token 认证
 */
export async function updateNotificationEmail(
  data: UpdateNotificationEmailRequest
): Promise<ApiResult<UserSettings>> {
  return put<UserSettings>('/settings/notification-email', data, {
    requiresAuth: true,
  });
}

/**
 * 更新邮件订阅开关
 *
 * 对应 OpenAPI: PUT /settings/email-subscription（UpdateEmailSubscriptionRequest）
 *
 * 关闭后，积分低余额告警、USDT 低余额告警、续费失败告警均不会发送。
 * 设置新的告警邮箱时（{@link updateNotificationEmail}）会自动重新开启订阅。
 *
 * @param data.subscribed true = 开启邮件通知，false = 关闭
 * @returns `{ emailSubscribed }` 当前最新订阅状态
 * @requiresAuth 需要 Bearer Token 认证
 */
export async function updateEmailSubscription(
  data: UpdateEmailSubscriptionRequest
): Promise<ApiResult<UpdateEmailSubscriptionResult>> {
  return put<UpdateEmailSubscriptionResult>(
    '/settings/email-subscription',
    data,
    { requiresAuth: true }
  );
}
